import React, { useMemo, useState } from 'react';
import { Button, alpha } from '@mui/material';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import BulkActionBar from './BulkActionBar';
import BulkUpdateDialog from './BulkUpdateDialog';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import ConfirmRejectDialog from './ConfirmRejectDialog';
import { ACCENT, DANGER } from './SaaSTokens';

/**
 * Bulk selection toolbar + update/delete dialogs for list pages.
 */
const ListBulkToolbar = ({
  config,
  currentUser,
  selectedIds,
  onClearSelection,
  onSuccess,
  onDeleteSuccess,
  onRejectSuccess,
  onError,
}) => {
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [rejectError, setRejectError] = useState(null);

  const dialogConfig = useMemo(
    () => (config ? { ...config, user: currentUser } : null),
    [config, currentUser],
  );

  const canBulk = config?.canBulk?.(currentUser);
  const canBulkDelete = (config?.canBulkDelete ?? config?.canBulk)?.(currentUser);
  const canBulkReject = config?.canBulkReject?.(currentUser);

  if (!config || (!canBulk && !canBulkDelete && !canBulkReject)) return null;
  if (!selectedIds?.length) return null;

  const label = config.entityLabel || 'record';
  const count = selectedIds.length;
  const hasUpdateFields = Boolean(config.fields);

  const handleBulkDelete = async () => {
    if (!onDeleteSuccess) return;
    try {
      setDeleteLoading(true);
      await onDeleteSuccess();
      setDeleteDialogOpen(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleBulkUpdateError = (message) => {
    setUpdateDialogOpen(false);
    onError?.(message);
  };

  const handleBulkReject = async (reason) => {
    if (!onRejectSuccess) return;
    try {
      setRejectLoading(true);
      setRejectError(null);
      await onRejectSuccess(reason);
      setRejectDialogOpen(false);
    } catch (err) {
      const msg =
        err?.response?.data?.message
        || Object.values(err?.response?.data?.errors || {}).flat()?.[0]
        || 'Failed to reject selected claims.';
      setRejectError(msg);
    } finally {
      setRejectLoading(false);
    }
  };

  const rejectButtonLabel = config?.bulkRejectButtonLabel || 'Bulk reject';

  const actionVerb = config?.bulkDeleteActionVerb || 'Delete';
  const actionVerbLower = actionVerb.toLowerCase();
  const ActionIcon = actionVerbLower === 'cancel'
    ? CancelOutlinedIcon
    : actionVerbLower === 'archive'
      ? ArchiveOutlinedIcon
      : DeleteOutlineIcon;
  const actionButtonLabel = config?.bulkDeleteButtonLabel || `Bulk ${actionVerbLower}`;
  const actionDescription = config?.bulkDeleteDescription
    || `This will permanently ${actionVerbLower} ${count} selected ${label}${count === 1 ? '' : 's'}. This action cannot be undone.`;
  const actionConfirmLabel = config?.bulkDeleteConfirmLabel || `${actionVerb} ${count}`;
  const actionTitle = config?.bulkDeleteTitle || `${actionVerb} ${count} ${label}${count === 1 ? '' : 's'}?`;

  return (
    <>
      <BulkActionBar
        count={count}
        label={count === 1 ? `${label} selected` : `${label}s selected`}
        onClear={onClearSelection}
      >
        {canBulk && hasUpdateFields && (
          <Button
            variant="contained"
            size="small"
            startIcon={<DriveFileRenameOutlineIcon />}
            onClick={() => setUpdateDialogOpen(true)}
            sx={{
              bgcolor: alpha(ACCENT, 0.28),
              color: '#e5e7eb',
              '&:hover': { bgcolor: alpha(ACCENT, 0.42) },
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
            }}
          >
            Bulk update
          </Button>
        )}
        {canBulkReject && onRejectSuccess && (
          <Button
            variant="contained"
            size="small"
            startIcon={<CancelOutlinedIcon />}
            onClick={() => {
              setRejectError(null);
              setRejectDialogOpen(true);
            }}
            sx={{
              bgcolor: alpha(DANGER, 0.18),
              color: '#fecaca',
              '&:hover': { bgcolor: alpha(DANGER, 0.32) },
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
            }}
          >
            {rejectButtonLabel}
          </Button>
        )}
        {canBulkDelete && onDeleteSuccess && (
          <Button
            variant="contained"
            size="small"
            startIcon={<ActionIcon />}
            onClick={() => setDeleteDialogOpen(true)}
            sx={{
              bgcolor: alpha(DANGER, 0.22),
              color: '#fecaca',
              '&:hover': { bgcolor: alpha(DANGER, 0.38) },
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
            }}
          >
            {actionButtonLabel}
          </Button>
        )}
      </BulkActionBar>

      {canBulk && hasUpdateFields && (
        <BulkUpdateDialog
          open={updateDialogOpen}
          onClose={() => setUpdateDialogOpen(false)}
          selectedIds={selectedIds}
          config={dialogConfig}
          onError={typeof onError === 'function' ? handleBulkUpdateError : undefined}
          onSuccess={(updates) => {
            onSuccess?.(updates);
            setUpdateDialogOpen(false);
          }}
        />
      )}

      {canBulkDelete && onDeleteSuccess && (
        <ConfirmDeleteDialog
          open={deleteDialogOpen}
          onClose={() => !deleteLoading && setDeleteDialogOpen(false)}
          onConfirm={handleBulkDelete}
          title={actionTitle}
          itemType={label}
          itemName=""
          description={actionDescription}
          confirmLabel={actionConfirmLabel}
          loading={deleteLoading}
        />
      )}

      {canBulkReject && onRejectSuccess && (
        <ConfirmRejectDialog
          open={rejectDialogOpen}
          onClose={() => !rejectLoading && setRejectDialogOpen(false)}
          onConfirm={handleBulkReject}
          title={`Reject ${count} ${label}${count === 1 ? '' : 's'}?`}
          description="Rejected claims remain on record. Only pending claims in your selection will be rejected."
          confirmLabel={`Reject ${count}`}
          loading={rejectLoading}
          errorMessage={rejectError}
          count={count}
        />
      )}
    </>
  );
};

export default ListBulkToolbar;
